<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="total-stat">
    <div class="total-stat-count">
      <EcsInteractionsPiechart
        v-if="slide.showAsGraph && slide.interactionsTypeList.length > 1"
        :interactions="interactions"
        :class="alertType"
      />
      <EcsInteractionsHistogram
        v-if="slide.showAsGraph && slide.interactionsTypeList.length === 1"
        :bps="businessProcesses"
      />
      <span v-if="!slide.showAsGraph" :class="alertType">{{ kpiFormat(interactions, 'number') }}</span>
    </div>
    <div v-if="!slide.showAsGraph" class="total-stat-description">
      <span v-for="(status, i) in slide.interactionsTypeList" :key="status">
        {{ status }}<span v-if="i < slide.interactionsTypeList.length - 1">, </span>
      </span>
    </div>

    <div class="total-stat-description total-stat-description--media-type">
      <span v-for="(media, i) in slide.mediaTypes" :key="media">
        {{ media }}<span v-if="i < slide.mediaTypes.length - 1">, </span>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsInteractionsPiechart from './EcsInteractionsPiechart.vue';
import EcsInteractionsHistogram from './EcsInteractionsHistogram.vue';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';

export default {
  name: 'EcsInteractionsSlide',
  components: {
    EcsInteractionsPiechart,
    EcsInteractionsHistogram
  },
  mixins: [EcsKpiFormatter],

  props: {
    slide: {
      type: Object,
      default() {
        return {};
      }
    }
  },

  computed: {
    ...mapGetters('WALLBOARD', ['getBpsByIds', 'getStatisticsByBpids', 'getSlidesInterval']),

    alertType() {
      const POSSIBLE_STATES_TYPES = [
        ['abandonedInteractions', 'abandoned'],
        ['answeredInteractions', 'answered'],
        ['overflowedInteractions', 'overflow'],
        ['totalQueuedInteractions', 'queued'],
        ['waitingInteractions', 'waiting']
      ];
      let slideAlertType = 'None';
      const interactionTypeList = this.slide.interactionsTypeList.map(interaction => interaction.toLowerCase());

      if (this.selectedBusinessProcessesStats) {
        POSSIBLE_STATES_TYPES.every(stateType => {
          if (!interactionTypeList.includes(stateType[1])) {
            return true;
          }

          if (this.selectedBusinessProcessesStats[stateType[0]].alertType === 'Alert') {
            slideAlertType = this.selectedBusinessProcessesStats[stateType[0]].alertType;
            return false;
          } else if (this.selectedBusinessProcessesStats[stateType[0]].alertType === 'Warning') {
            slideAlertType = this.selectedBusinessProcessesStats[stateType[0]].alertType;
          }
          return true;
        });
      }
      return 'alert-type-' + slideAlertType.toLowerCase();
    },

    selectedBusinessProcessesStats() {
      return this.getStatisticsByBpids(this.slide.businessProcesses);
    },

    businessProcesses() {
      const POSSIBLE_STATES_TYPES = [
        ['abandonedInteractions', 'abandoned'],
        ['answeredInteractions', 'answered'],
        ['overflowedInteractions', 'overflow'],
        ['totalQueuedInteractions', 'queued'],
        ['waitingInteractions', 'waiting']
      ];
      const mediaTypes = this.slide.mediaTypes.map(mediaType => mediaType.toLowerCase());
      const bps = this.getBpsByIds(this.slide.businessProcesses);

      const stateType = POSSIBLE_STATES_TYPES.find(
        stateType => stateType[1] === this.slide.interactionsTypeList[0].toLowerCase()
      )[0];
      return bps.map(bp => {
        return {
          name: bp.businessProcessName,
          value: this.getTotalInteractionsByMediaType(bp[stateType].value, mediaTypes)
        };
      });
    },

    interactions() {
      /* Prevent case sensitive issues */
      const interactionTypeList = this.slide.interactionsTypeList.map(interaction => interaction.toLowerCase());
      const mediaTypes = this.slide.mediaTypes.map(mediaType => mediaType.toLowerCase());

      if (!this.selectedBusinessProcessesStats) {
        return this.slide;
      }

      const totalInteractions = this.getFilteredInteractions(
        this.selectedBusinessProcessesStats,
        interactionTypeList,
        mediaTypes
      );

      if (this.slide.showAsGraph) {
        return totalInteractions;
      }

      return Object.keys(totalInteractions).reduce((acc, interaction) => acc + totalInteractions[interaction], 0);
    }
  },

  methods: {
    /**
     * Return the total interactions of a business process filtered by interaction type and media type
     *
     * @param {object} stats A processes Object of Selected Business Processes Stats
     * @param {string[]} interactionsTypeList The Interactions filter list
     * @param {string[]} mediaTypes The Media types filter list
     */
    getFilteredInteractions(stats, interactionsTypeList, mediaTypes) {
      const initialData = {};
      const POSSIBLE_INTERACTION_TYPES = {
        abandoned: stats.abandonedInteractions.value,
        answered: stats.answeredInteractions.value,
        overflow: stats.overflowedInteractions.value,
        queued: stats.totalQueuedInteractions.value,
        waiting: stats.waitingInteractions.value
      };

      return interactionsTypeList.reduce((acc, interaction) => {
        acc[interaction] = this.getTotalInteractionsByMediaType(POSSIBLE_INTERACTION_TYPES[interaction], mediaTypes);

        return acc;
      }, initialData);
    },

    getTotalInteractionsByMediaType(interaction, mediaTypes) {
      return interaction.dimensionsValues.reduce((total, current) => {
        if (!mediaTypes.includes(current.name.toLowerCase())) {
          return total;
        }

        return total + current.value;
      }, 0);
    }
  }
};
</script>

<style src="../scoped.css" scoped></style>
